import { z } from 'zod';

/**
 * Session + auth payloads. Tokens live in HttpOnly+Secure+SameSite cookies
 * (decisions-log #15); these schemas describe the request/response bodies the
 * auth API exchanges with the SPA, not the cookie contents themselves.
 */

export const SignupInput = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(256),
  displayName: z.string().max(80).optional(),
  /** Cloudflare Turnstile token — optional this session (D-004), required later. */
  turnstileToken: z.string().optional(),
});
export type SignupInput = z.infer<typeof SignupInput>;

export const LoginInput = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof LoginInput>;

export const VerifyEmailInput = z.object({
  email: z.string().email(),
  code: z.string().regex(/^\d{6}$/, 'expected a 6-digit code'),
});
export type VerifyEmailInput = z.infer<typeof VerifyEmailInput>;

export const ForgotPasswordInput = z.object({
  email: z.string().email(),
});
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordInput>;

export const ResetPasswordInput = z.object({
  email: z.string().email(),
  code: z.string().regex(/^\d{6}$/),
  newPassword: z.string().min(8).max(256),
});
export type ResetPasswordInput = z.infer<typeof ResetPasswordInput>;

export const ChangePasswordInput = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8).max(256),
});
export type ChangePasswordInput = z.infer<typeof ChangePasswordInput>;

/** Challenge returned by login when MFA / new-password is required. */
export const AuthChallenge = z.object({
  challengeName: z.enum(['SOFTWARE_TOKEN_MFA', 'NEW_PASSWORD_REQUIRED', 'MFA_SETUP']),
  session: z.string(),
});
export type AuthChallenge = z.infer<typeof AuthChallenge>;

/** TOTP MFA verify (login-time challenge response). */
export const MfaVerifyInput = z.object({
  session: z.string(),
  code: z.string().regex(/^\d{6}$/),
  /** Username (email) the challenge is for — required by Cognito's
      RespondToAuthChallenge for SOFTWARE_TOKEN_MFA. */
  email: z.string().email(),
});
export type MfaVerifyInput = z.infer<typeof MfaVerifyInput>;

/** The minimal identity the SPA holds in memory after auth (never the tokens). */
export const SessionUser = z.object({
  sub: z.string(),
  email: z.string().email(),
  displayName: z.string().optional(),
  isOperatorAdmin: z.boolean().default(false),
  mfaEnabled: z.boolean().default(false),
});
export type SessionUser = z.infer<typeof SessionUser>;
